import { Anchor, Box, Text } from '@mantine/core';
import { rowLabel, type RowLabel } from '../../domain/rowLabel.js';

export type RecordLabelProps = {
    title: string | null | undefined;
    entityKey: string;
    /** `inline` (the default): name then id, dimmed, on one line — a push row or card. `stacked`: name above id — a grid cell. */
    layout?: 'inline' | 'stacked';
    /** `stacked` only: caps the block's width, truncating with an ellipsis (and a `title` tooltip). */
    maw?: number;
    /** The primary line's size. Defaults per layout: `xs` inline, `sm` stacked. */
    size?: 'xs' | 'sm';
    /** `stacked` only: the primary line becomes a clickable anchor — the inbox's row opening the entity drawer. */
    onOpen?: () => void;
    /** `stacked` only: the rail's tighter type (12.5 over 10.5), where `size` is too coarse. */
    compact?: boolean;
    /** `stacked` only: the primary line at weight 700 — the rail's selected row. */
    strong?: boolean;
};

/**
 * A record's label — the name, then its id in monospace, dimmed. With no name the id alone, in
 * monospace. `rowLabel` decides which. `inline` runs the two on one line (a push row, a card);
 * `stacked` (was `Matrix/MatrixHeader.tsx`'s `StackedLabel`) puts the id on its own line beneath,
 * for a grid cell or a card's own header, where the two can wrap independently.
 */
export const RecordLabel = ({
    title,
    entityKey,
    layout = 'inline',
    maw,
    size,
    onOpen,
    compact,
    strong,
}: RecordLabelProps) => {
    const label = rowLabel(title, entityKey);

    if (layout === 'stacked') {
        return (
            <Box maw={maw} miw={0}>
                {onOpen ? (
                    <Anchor
                        fz={compact ? 12.5 : (size ?? 'sm')}
                        fw={strong ? 700 : 600}
                        truncate
                        onClick={onOpen}
                        className={label.labelIsId ? 'rgs-numeric' : undefined}
                        title={label.label}
                    >
                        {label.label}
                    </Anchor>
                ) : (
                    <Text
                        fz={compact ? 12.5 : (size ?? 'sm')}
                        fw={strong ? 700 : 600}
                        truncate
                        className={label.labelIsId ? 'rgs-numeric' : undefined}
                        title={label.label}
                    >
                        {label.label}
                    </Text>
                )}
                {label.secondary && (
                    <Text
                        fz={compact ? 10.5 : 'xs'}
                        c="dimmed"
                        className="rgs-numeric"
                        truncate
                        title={label.secondary}
                    >
                        {label.secondary}
                    </Text>
                )}
            </Box>
        );
    }

    return (
        <Text fz={size ?? 'xs'} fw={500} style={{ wordBreak: 'break-word' }}>
            <span className={label.labelIsId ? 'rgs-numeric' : undefined}>{label.label}</span>
            {label.secondary && (
                <Text span fz="xs" fw={400} c="dimmed" className="rgs-numeric" ml={6}>
                    {label.secondary}
                </Text>
            )}
        </Text>
    );
};

/**
 * The bare inline form of an already-computed `RowLabel` — name then id, dimmed — with no
 * wrapping block, for prose that already sits inside its own `Text`/`Title`: a modal's title
 * (`useUnsavedGuard`'s "Discard changes to…?"), a card's dim subtitle (`GameCards`' studio
 * line) or a breadcrumb (`Header`, which fades the id further still via `secondaryOpacity` —
 * the crumb it follows is already dimmed, so the id needs to read fainter yet). `RecordLabel`
 * itself wraps `inline` in a block `Text`, which is right for a diff row or a push card that
 * stands alone but would nest a block inside one here.
 */
export const RowLabelInline = ({ label, secondaryOpacity }: { label: RowLabel; secondaryOpacity?: number }) => (
    <>
        <span className={label.labelIsId ? 'rgs-numeric' : undefined}>{label.label}</span>
        {label.secondary && (
            <Text
                span
                inherit
                c="dimmed"
                className="rgs-numeric"
                ml={6}
                style={secondaryOpacity === undefined ? undefined : { opacity: secondaryOpacity }}
            >
                {label.secondary}
            </Text>
        )}
    </>
);
